import { useEffect, useMemo, useRef } from "react";
import type { Ref } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Shell from "../components/Shell";
import KundeAnsicht from "../components/kunde/KundeAnsicht";
import {
  usePflegekunden, stufe, aktuellerVertrag, pflegeMonate, monatKurz, STUFE_LABEL, STUFE_TON,
} from "../hooks/usePflegekunden";
import type { PflegeKunde, Stufe } from "../hooks/usePflegekunden";
import { eur } from "../hooks/useVertraege";
import "../styles/kunden.css";

const FILTER: { key: "alle" | Stufe; label: string }[] = [
  { key: "alle", label: "Alle" },
  { key: "interessent", label: "Interessenten" },
  { key: "angebot", label: "Im Angebot" },
  { key: "laufend", label: "Laufend" },
  { key: "kuendigung", label: "Kündigung läuft" },
  { key: "ehemalig", label: "Ehemalige" },
];

/**
 * Kunden — der Einstieg der Pflege (Etappe B, 09.10.2026), gebaut wie Kontors
 * Kundenseite: Liste links, Kunde rechts, am Telefon nacheinander. Der gewählte
 * Kunde und der Filter stehen in der Adresse (?kunde=…&filter=…), damit „Zurück"
 * aus einem Vertrag wieder bei genau diesem Kunden landet.
 *
 * Ersetzt „Interessenten": die sind hier ein Filter unter mehreren.
 */
export default function KundenPage() {
  const { data: kunden = [], isLoading, isError } = usePflegekunden();
  const [params, setParams] = useSearchParams();
  const filter = (params.get("filter") || "alle") as "alle" | Stufe;
  const gewaehlt = params.get("kunde") || "";
  const q = params.get("q") || "";
  const zeileRef = useRef<HTMLDivElement>(null);

  function setze(werte: Record<string, string>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(werte)) { if (v) p.set(k, v); else p.delete(k); }
    setParams(p, { replace: !("kunde" in werte) });
  }

  const zaehler = useMemo(() => {
    const z: Record<string, number> = { alle: kunden.length };
    for (const k of kunden) z[stufe(k)] = (z[stufe(k)] || 0) + 1;
    return z;
  }, [kunden]);

  const liste = useMemo(() => {
    const s = q.trim().toLowerCase();
    return kunden.filter(k => (filter === "alle" || stufe(k) === filter)
      && (!s || `${k.name} ${k.ort} ${k.strasse} ${k.email} ${k.vertraege.map(v => v.nummer).join(" ")}`.toLowerCase().includes(s)));
  }, [kunden, filter, q]);

  const kunde = kunden.find(k => k.id === gewaehlt);
  useEffect(() => {
    if (kunde) zeileRef.current?.scrollIntoView({ block: "nearest" });
  }, [kunde]);

  return (
    <Shell title="Kunden" breit
      subtitle={`${kunden.length} Pflegekunden · ${zaehler.laufend || 0} laufend · ${zaehler.interessent || 0} Interessenten`}
      action={<Link className="pf-btn" to="/vertraege/neu">＋ Neuer Vertrag</Link>}>
      <div className={"pf-split" + (kunde ? " ist-gewaehlt" : "")}>
        <div className="pf-split-liste">
          <input className="pf-input pf-suche" value={q} onChange={e => setze({ q: e.target.value })}
            placeholder="Name, Ort, E-Mail oder Vertragsnummer …" aria-label="Kunden durchsuchen" />
          <div className="pf-chips" role="group" aria-label="Nach Stand filtern">
            {FILTER.filter(f => f.key === "alle" || (zaehler[f.key] || 0) > 0 || f.key === filter).map(f => (
              <button key={f.key} type="button" className={"pf-chip" + (filter === f.key ? " is-on" : "")}
                aria-pressed={filter === f.key} onClick={() => setze({ filter: f.key === "alle" ? "" : f.key })}>
                {f.label} <span className="pf-chip-zahl">{zaehler[f.key] || 0}</span>
              </button>
            ))}
          </div>
          {isLoading ? <div className="pf-leer">Lade Kunden…</div>
            : isError ? <div className="pf-leer">Die Kunden ließen sich nicht laden.</div>
            : liste.length === 0 ? <div className="pf-leer">{q ? `Niemand passt zu „${q}“.` : "Hier steht noch niemand."}</div>
            : (
              <div className="pf-kundenliste" role="list">
                {liste.map(k => (
                  <KundenZeile key={k.id} k={k} an={k.id === gewaehlt}
                    zeileRef={k.id === gewaehlt ? zeileRef : undefined}
                    onWahl={() => setze({ kunde: k.id })} />
                ))}
              </div>
            )}
        </div>

        <div className="pf-split-detail">
          {kunde ? <KundeAnsicht key={kunde.id} k={kunde} onSchliessen={() => setze({ kunde: "" })} /> : (
            <div className="pf-leerblatt">
              <h3>Kein Kunde ausgewählt</h3>
              <p>
                Wähle links einen Kunden. Rechts siehst du dann alles, was die Pflege an ihm
                angeht — Verträge, Raten, Garten, Einsätze und den Kundenzugang. Stammdaten
                änderst du wie gewohnt in Kontor.
              </p>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function KundenZeile({ k, an, onWahl, zeileRef }: {
  k: PflegeKunde; an: boolean; onWahl: () => void; zeileRef?: Ref<HTMLDivElement>;
}) {
  const st = stufe(k);
  const v = aktuellerVertrag(k);
  const monate = pflegeMonate(k).map(([m]) => monatKurz(m)).join(" · ");
  return (
    <div ref={zeileRef} role="listitem" className={"pf-kundenzeile" + (an ? " is-an" : "")}>
      <button type="button" className="pf-kundenzeile-knopf" onClick={onWahl} aria-current={an ? "true" : undefined}>
        <span className="pf-kundenzeile-name">{k.name}</span>
        <span className="pf-kundenzeile-unter">
          {[k.ort, v ? (v.status === "entwurf" ? "Entwurf" : v.nummer) : monate].filter(Boolean).join(" · ") || "—"}
        </span>
      </button>
      <span className="pf-kundenzeile-rechts">
        {v?.monats_netto ? <span className="pf-kundenzeile-betrag">{eur(v.monats_netto)}</span> : null}
        <span className={"pf-pill " + STUFE_TON[st]}>{STUFE_LABEL[st]}</span>
      </span>
    </div>
  );
}
